import React from 'react';
import { useChartTooltip } from './useChartTooltip.jsx';

// Brier per category, pooled across every set — which subjects the forecasts are actually good at.
//
// Horizontal, because the labels are words ("Global Politics:", "Uncategorised") and rotating them
// under a column chart to save space is how axis labels become unreadable. Sorted best-first, so
// the answer to "where am I worst?" is always the bottom bar.
//
// Each bar carries its own base-rate baseline as a tick. A category can have a low Brier simply by
// being easy — a year of "I don't get injured" at 0.9 scores well without any skill involved — and
// the tick is what separates that from forecasting well.

// Same 420 width as the other three so the type scale matches across the grid; the height grows
// with the number of categories instead, which is the one thing a horizontal bar chart can't fix.
const WIDTH = 420;
const PAD = { top: 26, right: 88, bottom: 42, left: 104 };
const ROW_H = 26;
const BAR_H = 14;
const PLOT_W = WIDTH - PAD.left - PAD.right;

// Two right-aligned columns past the bars, each under its own heading. The count belongs on the
// chart rather than only in the tooltip because it is what says how much to trust a bar: `Misc` is
// the best score here and it is seven predictions.
const BRIER_X = WIDTH - 50;
const COUNT_X = WIDTH - 6;

const MAX_LABEL = 15;

function truncate(name) {
  return name.length > MAX_LABEL ? `${name.slice(0, MAX_LABEL - 1)}…` : name;
}

export default function CategoryBrierChart({ rows }) {
  const tip = useChartTooltip();
  const scored = (rows || []).filter(row => row.brier !== null && row.brier !== undefined);
  if (scored.length < 2) return null;

  const sorted = [...scored].sort((a, b) => a.brier - b.brier);
  const height = PAD.top + sorted.length * ROW_H + PAD.bottom;
  const plotBottom = PAD.top + sorted.length * ROW_H;

  const values = sorted.flatMap(r => [r.brier, r.baseline]).filter(v => v !== null && v !== undefined);
  const xMax = Math.max(0.1, Math.ceil((Math.max(...values) + 0.02) * 20) / 20);
  const xScale = v => PAD.left + (v / xMax) * PLOT_W;

  const step = xMax > 0.2 ? 0.1 : 0.05;
  const ticks = [];
  for (let t = 0; t <= xMax + 1e-9; t += step) ticks.push(Math.round(t * 1000) / 1000);

  return (
    <figure className="pred-chart" ref={tip.ref}>
      <figcaption className="pred-chart-title">Brier score by category</figcaption>
      <p className="pred-legend">
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-primary" aria-hidden="true" />
          Brier score
        </span>
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-marker" aria-hidden="true" />
          Base-rate baseline
        </span>
        <span className="pred-legend-note">shorter is better</span>
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="pred-chart-svg"
        role="img"
        aria-label="Brier score for each category, pooled across sets, best first"
      >
        <text className="pred-chart-axis" x={BRIER_X} y={PAD.top - 10} textAnchor="end">
          Brier
        </text>
        <text className="pred-chart-axis" x={COUNT_X} y={PAD.top - 10} textAnchor="end">
          Graded
        </text>

        {ticks.map(t => (
          <g key={t}>
            <line
              className="pred-chart-grid"
              x1={xScale(t)}
              x2={xScale(t)}
              y1={PAD.top}
              y2={plotBottom}
            />
            <text className="pred-chart-tick" x={xScale(t)} y={plotBottom + 16} textAnchor="middle">
              {t.toFixed(2)}
            </text>
          </g>
        ))}

        {sorted.map((row, i) => {
          const y = PAD.top + i * ROW_H + (ROW_H - BAR_H) / 2;
          const width = Math.max(2, xScale(row.brier) - PAD.left);
          return (
            <g key={row.name}>
              <text
                className="pred-chart-tick"
                x={PAD.left - 10}
                y={y + BAR_H / 2 + 4}
                textAnchor="end"
              >
                {truncate(row.name)}
              </text>
              <rect className="pred-chart-bar" x={PAD.left} y={y} width={width} height={BAR_H} rx="3" />
              {row.baseline !== null && row.baseline !== undefined && (
                <line
                  className="pred-chart-marker"
                  x1={xScale(row.baseline)}
                  x2={xScale(row.baseline)}
                  y1={y - 3}
                  y2={y + BAR_H + 3}
                />
              )}
              {/* Values live in fixed right-hand columns rather than trailing each bar: floating
                  them puts a number on top of that row's baseline tick, and a column of scores
                  lines up to be read against each other. */}
              <text className="pred-chart-value" x={BRIER_X} y={y + BAR_H / 2 + 4} textAnchor="end">
                {row.brier.toFixed(3)}
              </text>
              <text className="pred-chart-tick" x={COUNT_X} y={y + BAR_H / 2 + 4} textAnchor="end">
                {row.scored}
              </text>
              {/* The whole row is the hit target, label and value included — the bar alone is 14px. */}
              <rect
                x={0}
                y={PAD.top + i * ROW_H}
                width={WIDTH}
                height={ROW_H}
                fill="transparent"
                {...tip.bind({
                  title: row.name,
                  rows: [
                    ['Brier', row.brier.toFixed(3)],
                    ['Baseline', row.baseline === null || row.baseline === undefined ? null : row.baseline.toFixed(3)],
                    ['Graded', row.scored],
                    [
                      'Happened',
                      row.yesRate === null || row.yesRate === undefined
                        ? row.happened
                        : `${row.happened} (${Math.round(row.yesRate * 100)}%)`
                    ]
                  ]
                })}
              />
            </g>
          );
        })}

        <text className="pred-chart-axis" x={PAD.left + PLOT_W / 2} y={height - 8} textAnchor="middle">
          Brier score
        </text>
      </svg>
      {tip.node}
      <p className="pred-chart-note">
        Pooled over every selected set. A bar shorter than its baseline tick is a category where the
        probabilities carried real information.
      </p>
    </figure>
  );
}
